/**
 * RSVP Data Service
 * 
 * Centralized service for extracting and processing RSVP data across the application.
 * This eliminates code duplication and provides consistent RSVP data handling.
 */

/**
 * Extract RSVP data from user data structure
 * Handles multiple data sources and formats consistently
 * 
 * @param {Object} userData - User document data from Firestore
 * @param {Object} eventsConfig - Optional events configuration (legacy support)
 * @returns {Object} RSVP data with Going, Maybe, and Decline arrays
 */
export const extractRsvpDataFromUserData = (userData, eventsConfig) => {
    let rsvpCounts = { Going: [], Maybe: [], Decline: [] };
    
    if (!userData) {
        return rsvpCounts;
    }
    
    // Check eventsPageConfig cards first (most likely location)
    const eventsCards = userData?.pages?.eventsPageConfig?.cards || [];
    
    if (Array.isArray(eventsCards) && eventsCards.length > 0) {
        eventsCards.forEach((card) => {
            if (card.type === 'RSVP') {
                // Merge arrays, ensuring we don't get duplicates
                rsvpCounts.Going = [...(rsvpCounts.Going || []), ...(card.going || [])];
                rsvpCounts.Maybe = [...(rsvpCounts.Maybe || []), ...(card.maybe || [])];
                rsvpCounts.Decline = [...(rsvpCounts.Decline || []), ...(card.decline || [])];
            }
        });
    }
    
    // Check top-level cards as fallback
    const topLevelCards = userData?.cards || [];
    if (topLevelCards.length > 0) {
        topLevelCards.forEach((card) => {
            if (card.type === 'RSVP') {
                rsvpCounts.Going = [...(rsvpCounts.Going || []), ...(card.going || [])];
                rsvpCounts.Maybe = [...(rsvpCounts.Maybe || []), ...(card.maybe || [])];
                rsvpCounts.Decline = [...(rsvpCounts.Decline || []), ...(card.decline || [])];
            }
        });
    }
    
    // Check legacy RSVP format in eventsConfig as final fallback
    if (eventsConfig) {
        const legacyRsvpData = eventsConfig.rsvp || {};
        if (legacyRsvpData.Going || legacyRsvpData.Maybe || legacyRsvpData.Decline) {
            rsvpCounts.Going = [...(rsvpCounts.Going || []), ...(legacyRsvpData.Going || [])];
            rsvpCounts.Maybe = [...(rsvpCounts.Maybe || []), ...(legacyRsvpData.Maybe || [])];
            rsvpCounts.Decline = [...(rsvpCounts.Decline || []), ...(legacyRsvpData.Decline || [])];
        }
        
        // Check direct RSVP properties in eventsConfig
        if (eventsConfig.going || eventsConfig.maybe || eventsConfig.decline) {
            rsvpCounts.Going = [...(rsvpCounts.Going || []), ...(eventsConfig.going || [])];
            rsvpCounts.Maybe = [...(rsvpCounts.Maybe || []), ...(eventsConfig.maybe || [])];
            rsvpCounts.Decline = [...(rsvpCounts.Decline || []), ...(eventsConfig.decline || [])];
        }
    }
    
    return rsvpCounts;
};

/**
 * Process RSVP entry - handles both email strings and user objects
 * 
 * @param {string|Object} entry - RSVP entry (email string or user object)
 * @param {string} status - RSVP status (Going, Maybe, Decline)
 * @returns {Object|null} Processed user object or null if invalid
 */
export const processRsvpEntry = (entry, status) => {
    if (typeof entry === 'string') {
        // Handle email string entries (like in decline array)
        return {
            uid: null,
            email: entry,
            firstName: '',
            lastName: '',
            profilePictureURL: null,
            status: status,
            timestamp: new Date().toISOString()
        };
    } else if (typeof entry === 'object' && entry !== null) {
        // Handle user object entries (like in going/maybe arrays)
        return {
            uid: entry.id || null,
            email: entry.email || '',
            firstName: entry.firstName || '',
            lastName: entry.lastName || '',
            profilePictureURL: entry.profilePictureURL || null,
            status: status,
            timestamp: entry.timestamp || new Date().toISOString()
        };
    }
    return null;
};

/**
 * Get processed RSVP users with status information
 * 
 * @param {Object} rsvpData - RSVP data from extractRsvpDataFromUserData
 * @returns {Array} Array of processed user objects with status
 */
export const getProcessedRsvpUsers = (rsvpData) => {
    const rsvpUsersWithStatus = [];
    
    // Process each RSVP status
    (rsvpData.Going || []).forEach(entry => {
        const user = processRsvpEntry(entry, 'Going');
        if (user) rsvpUsersWithStatus.push(user);
    });
    
    (rsvpData.Maybe || []).forEach(entry => {
        const user = processRsvpEntry(entry, 'Maybe');
        if (user) rsvpUsersWithStatus.push(user);
    });
    
    (rsvpData.Decline || []).forEach(entry => {
        const user = processRsvpEntry(entry, 'Decline');
        if (user) rsvpUsersWithStatus.push(user);
    });
    
    return rsvpUsersWithStatus;
};

/**
 * Get reminder-eligible recipients (Going and Maybe users only)
 * 
 * @param {Object} rsvpData - RSVP data from extractRsvpDataFromUserData
 * @returns {Array} Array of users eligible for reminders
 */
export const getReminderEligibleUsers = (rsvpData) => {
    const recipients = [];
    
    // Add Going users
    (rsvpData.Going || []).forEach(entry => {
        const user = processRsvpEntry(entry, 'Going');
        if (user && user.email) recipients.push(user);
    });
    
    // Add Maybe users
    (rsvpData.Maybe || []).forEach(entry => {
        const user = processRsvpEntry(entry, 'Maybe');
        if (user && user.email) recipients.push(user);
    });
    
    // Remove duplicates based on email
    const uniqueRecipients = recipients.filter((recipient, index, self) => 
        index === self.findIndex(r => r.email === recipient.email && r.email !== '')
    );
    
    return uniqueRecipients;
};

/**
 * Calculate RSVP statistics
 * 
 * @param {Object} rsvpData - RSVP data from extractRsvpDataFromUserData
 * @param {Array} sharedContacts - Optional shared contacts array
 * @returns {Object} RSVP statistics
 */
export const calculateRsvpStats = (rsvpData, sharedContacts = []) => {
    return {
        going: rsvpData?.Going?.length || 0,
        maybe: rsvpData?.Maybe?.length || 0,
        decline: rsvpData?.Decline?.length || 0,
        invited: sharedContacts?.length || 0,
        total: (rsvpData?.Going?.length || 0) + (rsvpData?.Maybe?.length || 0) + (rsvpData?.Decline?.length || 0)
    };
};

// Default export with all functions
const RSVPDataService = {
    extractRsvpDataFromUserData,
    processRsvpEntry,
    getProcessedRsvpUsers,
    getReminderEligibleUsers,
    calculateRsvpStats
};

export default RSVPDataService;